import React, { useState, useEffect, useImperativeHandle, forwardRef, useMemo } from 'react';
import ReactMarkdown from 'react-markdown';
import { fetchVocabularyFillInExercise, saveExerciseProgress, loadExerciseProgress, resetExerciseProgress, recordExerciseAttempt, getVocabularyExplanation } from '../../services/api';
import { useExerciseTimer } from '../../hooks/useExerciseTimer';
import { useAuth } from '../../contexts/AuthContext';
import Loading from '../Loading';
import Error from '../Error';
import VocabularyFillInResults from './VocabularyFillInResults';
import ExerciseLayout from '../shared/ExerciseLayout';
import ExerciseCard from '../shared/ExerciseCard';
import TranslatableText from '../shared/TranslatableText';
import { buildVocabularyFillinErrors } from './buildErrors';

const shuffleArray = (array) => {
  const shuffled = [...array];
  for (let i = shuffled.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
  }
  return shuffled;
};

const VocabularyFillInExercise = forwardRef(({ topic = 'groceries', conversationId = null, onBack }, ref) => {
  const { user } = useAuth();
  const [exercises, setExercises] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentExerciseIndex, setCurrentExerciseIndex] = useState(0);
  const [droppedWord, setDroppedWord] = useState(null);
  const [feedback, setFeedback] = useState(null); // 'correct', 'incorrect', null
  const [score, setScore] = useState(0);
  const [userResponses, setUserResponses] = useState({});
  const [showResults, setShowResults] = useState(false);
  const [engagementData, setEngagementData] = useState(null);
  const [engagementLoading, setEngagementLoading] = useState(false);
  const [revealedIndexes, setRevealedIndexes] = useState(new Set());
  const [explanation, setExplanation] = useState(null);
  const [explanationLoading, setExplanationLoading] = useState(false);
  const { timeSpent, stop: stopTimer } = useExerciseTimer();

  // Expose saveProgress method to parent component
  useImperativeHandle(ref, () => ({
    saveProgress: async () => {
      try {
        await saveExerciseProgress(topic, 'vocabulary_fillin', {
          currentExerciseIndex,
          userResponses,
          score,
          feedback,
          droppedWord,
          revealedIndexes: [...revealedIndexes],
          showResults,
          exerciseOrder: exercises.map(ex => ex.id)
        });
      } catch (err) {
        console.error('Error saving progress from ref:', err);
        throw err;
      }
    }
  }));

  useEffect(() => {
    const loadAll = async () => {
      try {
        setLoading(true);
        setError(null);

        const data = await fetchVocabularyFillInExercise({ topic, conversationId });
        let loadedExercises = data.exercises || [];

        // Try to restore previous order from saved progress, otherwise shuffle
        let savedProgress = null;
        try {
          savedProgress = await loadExerciseProgress(topic, 'vocabulary_fillin');
        } catch (err) {
          console.error('Error loading progress:', err);
        }

        const savedOrder = savedProgress?.progress_data?.exerciseOrder;
        if (Array.isArray(savedOrder) && savedOrder.length > 0) {
          const byId = new Map(loadedExercises.map(ex => [ex.id, ex]));
          const ordered = savedOrder.map(id => byId.get(id)).filter(Boolean);
          const orderedIds = new Set(savedOrder);
          const remaining = loadedExercises.filter(ex => !orderedIds.has(ex.id));
          loadedExercises = [...ordered, ...shuffleArray(remaining)];
        } else {
          loadedExercises = shuffleArray(loadedExercises);
        }

        setExercises(loadedExercises);

        if (savedProgress && savedProgress.progress_data) {
          const pd = savedProgress.progress_data;
          if (pd.currentExerciseIndex !== undefined) setCurrentExerciseIndex(pd.currentExerciseIndex);
          if (pd.userResponses) setUserResponses(pd.userResponses);
          if (pd.score !== undefined) setScore(pd.score);
          if (pd.feedback !== undefined) setFeedback(pd.feedback);
          if (pd.droppedWord !== undefined) setDroppedWord(pd.droppedWord);
          if (pd.revealedIndexes) setRevealedIndexes(new Set(pd.revealedIndexes));
          if (pd.showResults) setShowResults(true);
        }
      } catch (err) {
        console.error('Error fetching exercises:', err);
        setError(err.message || 'Failed to load exercises');
      } finally {
        setLoading(false);
      }
    };

    loadAll();
  }, [topic, conversationId]);

  const currentExercise = exercises[currentExerciseIndex];

  const shuffledOptions = useMemo(() => {
    if (!currentExercise?.options) return [];
    return shuffleArray(currentExercise.options);
  }, [currentExercise?.options]);

  const handleReveal = async () => {
    const correctAnswer = currentExercise.correct_answer;
    const exerciseId = currentExercise.id;
    const newRevealedIndexes = new Set([...revealedIndexes, currentExerciseIndex]);
    setRevealedIndexes(newRevealedIndexes);
    setDroppedWord(correctAnswer);
    setFeedback('incorrect');

    const newUserResponses = {
      ...userResponses,
      [currentExerciseIndex]: { answer: correctAnswer, correct: false, seen: true }
    };
    setUserResponses(newUserResponses);

    setExplanation(null);
    setExplanationLoading(true);
    getVocabularyExplanation(exerciseId)
      .then(result => {
        setExplanation(result?.explanation || null);
      })
      .catch(err => {
        console.error('Error loading vocabulary explanation:', err);
        setExplanation(null);
      })
      .finally(() => {
        setExplanationLoading(false);
      });

    try {
      await saveExerciseProgress(topic, 'vocabulary_fillin', {
        currentExerciseIndex,
        userResponses: newUserResponses,
        score,
        feedback: 'incorrect',
        droppedWord: correctAnswer,
        revealedIndexes: [...newRevealedIndexes],
        showResults: false,
        exerciseOrder: exercises.map(ex => ex.id)
      });
    } catch (err) {
      console.error('Error saving progress on reveal:', err);
    }
  };

  const handleWordClick = async (word) => {
    // Ignore clicks if already answered
    if (feedback !== null) return;

    setDroppedWord(word);

    // Store user response
    const isCorrect = word === currentExercise.correct_answer;
    const newUserResponses = {
      ...userResponses,
      [currentExerciseIndex]: {
        answer: word,
        correct: isCorrect
      }
    };
    setUserResponses(newUserResponses);

    // Vyhodnocení odpovědi
    const newScore = isCorrect ? score + 1 : score;
    if (isCorrect) {
      setFeedback('correct');
      setScore(newScore);
    } else {
      setFeedback('incorrect');
      setExplanation(null);
      setExplanationLoading(true);
      getVocabularyExplanation(currentExercise.id, word)
        .then(result => {
          setExplanation(result?.explanation || null);
        })
        .catch(err => {
          console.error('Error loading vocabulary explanation:', err);
          setExplanation(null);
        })
        .finally(() => {
          setExplanationLoading(false);
        });
    }

    // Uložit progress ihned po kliknutí na slovíčko
    try {
      await saveExerciseProgress(topic, 'vocabulary_fillin', {
        currentExerciseIndex,
        userResponses: newUserResponses,
        score: newScore,
        feedback: isCorrect ? 'correct' : 'incorrect',
        droppedWord: word,
        showResults: false,
        exerciseOrder: exercises.map(ex => ex.id)
      });
    } catch (err) {
      console.error('Error saving progress:', err);
    }
  };

  const handleNextExercise = async () => {
    if (currentExerciseIndex < exercises.length - 1) {
      const nextIndex = currentExerciseIndex + 1;
      setCurrentExerciseIndex(nextIndex);
      setDroppedWord(null);
      setFeedback(null);
      setExplanation(null);
      setExplanationLoading(false);
    } else {
      // Stop timer and get final time
      const finalTime = stopTimer();

      // Calculate errors from userResponses (keyed by array index → real Vocabulary.id)
      const errors = buildVocabularyFillinErrors(userResponses, exercises);

      // Show results when all exercises are completed
      setShowResults(true);

      // Record attempt in the background (fast - no LLM)
      setEngagementLoading(true);
      recordExerciseAttempt({
        topic: topic,
        exercise_type: 'vocabulary_fillin',
        score: score,
        max_score: exercises.length,
        time_spent_seconds: finalTime,
        errors: errors.length > 0 ? errors : null,
        exercise_ids: exercises.map(ex => ex.id)
      })
        .then(result => {
          setEngagementData(result);
          setEngagementLoading(false);
        })
        .catch(trackingError => {
          console.error('Failed to record exercise attempt:', trackingError);
          setEngagementLoading(false);
        });

      // Save progress with showResults flag
      try {
        await saveExerciseProgress(topic, 'vocabulary_fillin', {
          currentExerciseIndex,
          userResponses,
          score,
          feedback,
          droppedWord,
          revealedIndexes: [...revealedIndexes],
          showResults: true,
          errors: errors.length > 0 ? errors : null,
          exerciseOrder: exercises.map(ex => ex.id)
        });
      } catch (err) {
        console.error('Error saving progress on completion:', err);
      }
    }
  };

  const handleRestart = async () => {
    setCurrentExerciseIndex(0);
    setDroppedWord(null);
    setFeedback(null);
    setScore(0);
    setUserResponses({});
    setRevealedIndexes(new Set());
    setShowResults(false);
    setEngagementData(null);
    setEngagementLoading(false);
    setExplanation(null);
    setExplanationLoading(false);
    setExercises(prev => shuffleArray(prev));

    // Reset progress in database
    try {
      await resetExerciseProgress(topic, 'vocabulary_fillin');
    } catch (err) {
      console.error('Error resetting progress:', err);
    }
  };

  // Loading state
  if (loading) {
    return <Loading />;
  }

  // Error state
  if (error) {
    return <Error error={error} />;
  }

  // No exercises state
  if (!exercises || exercises.length === 0) {
    return (
      <ExerciseLayout
        title="Slovíčka"
        color="amber"
        onBack={onBack}
      >
        <ExerciseCard>
          <div className="text-center py-8 text-gray-500 text-lg">
            Žádná cvičení nejsou k dispozici
          </div>
        </ExerciseCard>
      </ExerciseLayout>
    );
  }

  // Results view
  if (showResults) {
    return (
      <VocabularyFillInResults
        exercises={exercises}
        userResponses={userResponses}
        restart={handleRestart}
        onBack={onBack}
        topic={topic}
        engagementData={engagementData}
        engagementLoading={engagementLoading}
      />
    );
  }

  return (
    <ExerciseLayout
      title="Slovíčka"
      subtitle="Doplňte správné slovíčko do věty"
      color="amber"
      icon="⚡"
      progress={{ current: currentExerciseIndex + 1, total: exercises.length }}
      onBack={onBack}
    >
      <ExerciseCard>
        {/* Header */}
        <div className="flex justify-between items-center mb-6">
          <div className="text-sm font-medium text-gray-500">
            Otazka {currentExerciseIndex + 1} / {exercises.length}
          </div>
          <div className="text-sm text-gray-700">
            Skore: <span className="font-semibold text-green-600">{score}</span> / {exercises.length}
          </div>
        </div>

        {/* Sentence with drop zone */}
        <div className="mb-8 p-6 bg-gray-50 rounded-xl text-xl text-center leading-relaxed">
          <TranslatableText text={currentExercise.sentence_before + ' '} textColor="#374151" topic={topic} />

          {/* Answer display */}
          <span
            className={`inline-block min-w-[150px] px-5 py-2 rounded-lg font-semibold transition-all ${
              droppedWord
                ? feedback === 'correct'
                  ? 'bg-green-100 text-green-700 border-2 border-green-400'
                  : revealedIndexes.has(currentExerciseIndex)
                    ? 'bg-blue-50 text-blue-700 border-2 border-blue-300'
                    : feedback === 'incorrect'
                      ? 'bg-red-100 text-red-600 border-2 border-red-400'
                      : 'bg-gray-100 text-gray-700'
                : 'bg-white text-gray-400 border-2 border-dashed border-amber-400'
            }`}
          >
            {droppedWord || '_______'}
          </span>

          <TranslatableText text={' ' + currentExercise.sentence_after} textColor="#374151" topic={topic} />

          {/* Hint */}
          {user?.support_level !== 'independent' && (
            <div className="mt-4 text-base text-gray-500 italic">
              <TranslatableText text={currentExercise.hint} textColor="#6b7280" topic={topic} />
            </div>
          )}
        </div>

        {/* Feedback */}
        {feedback && !revealedIndexes.has(currentExerciseIndex) && (
          <div className={`mb-6 p-3 rounded-lg text-center font-medium ${
            feedback === 'correct'
              ? 'bg-green-100 text-green-700 border border-green-300'
              : 'bg-red-100 text-red-600 border border-red-300'
          }`}>
            {feedback === 'correct' ? '✓ Správně!' : (
              <>✗ Špatně – správně je <strong>{currentExercise.correct_answer}</strong></>
            )}
          </div>
        )}

        {/* Options - clickable words */}
        <div className="flex gap-3 justify-center mb-4 flex-wrap">
          {shuffledOptions.map((option, index) => (
            <button
              key={index}
              onClick={() => handleWordClick(option)}
              disabled={feedback !== null}
              className={`px-6 py-3 rounded-lg text-lg font-medium transition-all ${
                feedback === null
                  ? 'bg-amber-500 hover:bg-amber-600 text-white shadow-md hover:shadow-lg hover:-translate-y-0.5 cursor-pointer'
                  : 'bg-gray-300 text-gray-500 cursor-not-allowed opacity-50'
              }`}
            >
              {option}
            </button>
          ))}
        </div>

        {/* Nevím button / Revealed answer */}
        <div className="flex justify-center mb-4">
          {user?.support_level !== 'independent' && feedback === null && (
            <button
              onClick={handleReveal}
              className="px-4 py-2 text-sm bg-gray-200 hover:bg-gray-300 text-gray-600 rounded-lg transition-all"
            >
              Nevím
            </button>
          )}
          {revealedIndexes.has(currentExerciseIndex) && feedback !== null && (
            <span className="text-sm text-blue-600 font-medium">
              Odpověď: {currentExercise.correct_answer}
            </span>
          )}
        </div>

        {/* AI vysvětlení slovíčka */}
        {feedback === 'incorrect' && (explanationLoading || explanation) && (
          <div className="mb-4 p-4 bg-amber-50 border border-amber-200 rounded-xl">
            <p className="text-sm font-medium text-amber-700 mb-2">Vysvětlení</p>
            {explanation ? (
              <div className="text-gray-700 leading-relaxed prose prose-sm max-w-none">
                <ReactMarkdown>{explanation}</ReactMarkdown>
              </div>
            ) : (
              <div className="animate-pulse space-y-2">
                <div className="h-4 bg-amber-200/60 rounded w-full" />
                <div className="h-4 bg-amber-200/60 rounded w-5/6" />
                <div className="h-4 bg-amber-200/60 rounded w-4/6" />
              </div>
            )}
          </div>
        )}

        {/* Navigation buttons */}
        <div className="flex gap-3 justify-center">
          {feedback !== null && (
            <button
              onClick={handleNextExercise}
              className="px-6 py-3 bg-green-500 hover:bg-green-600 text-white rounded-lg font-medium transition-all"
            >
              {currentExerciseIndex === exercises.length - 1 ? 'Zobrazit výsledky →' : 'Další slovíčko →'}
            </button>
          )}
        </div>
      </ExerciseCard>
    </ExerciseLayout>
  );
});

export default VocabularyFillInExercise;
